import { Suspense, lazy, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  LayoutDashboard, 
  TrendingUp, 
  Settings, 
  Activity, 
  ExternalLink,
  Handshake
} from 'lucide-react';
import { getHubSpotConfig, getHubSpotMeetingUrl } from '@/lib/hubspot';
import { useQuery } from '@tanstack/react-query';
import { 
  getLeadCaptureMetrics, 
  getUnitInterestAnalytics, 
  getDealSyncPerformance,
  getAllHubSpotAnalytics
} from '@/lib/admin/hubspot-analytics';
import { ConnectionStatusBadge } from '@/components/admin/hubspot/ConnectionStatusBadge';
import { TabLoadingSkeleton } from '@/components/admin/hubspot/TabLoadingSkeleton';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/use-auth';

// Lazy load tab components for better performance
const DashboardTab = lazy(() => import('@/components/admin/hubspot/DashboardTab').then(m => ({ default: m.DashboardTab })));
const AnalyticsTab = lazy(() => import('@/components/admin/hubspot/AnalyticsTab').then(m => ({ default: m.AnalyticsTab })));
const SystemTab = lazy(() => import('@/components/admin/hubspot/SystemTab').then(m => ({ default: m.SystemTab })));
const ActivityTab = lazy(() => import('@/components/admin/hubspot/ActivityTab').then(m => ({ default: m.ActivityTab })));
const ResourcesTab = lazy(() => import('@/components/admin/hubspot/ResourcesTab').then(m => ({ default: m.ResourcesTab })));
const DealManagementTab = lazy(() => import('@/components/admin/hubspot/DealManagementTab').then(m => ({ default: m.DealManagementTab })));

export default function AdminHubSpot() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  const { isAdmin } = useAuth();

  const hubspotConfig = getHubSpotConfig();
  const meetingUrl = getHubSpotMeetingUrl();
  const portalId = hubspotConfig?.portalId || '50726975';

  // Redirect non-admin users away from system/activity tabs
  useEffect(() => {
    if (!isAdmin && (activeTab === 'system' || activeTab === 'activity')) {
      setSearchParams({ tab: 'dashboard' });
    }
  }, [isAdmin, activeTab, setSearchParams]);

  // Fetch analytics data
  const { data: leadMetrics, isLoading: leadMetricsLoading } = useQuery({
    queryKey: ['hubspot-lead-metrics'],
    queryFn: getLeadCaptureMetrics,
    refetchInterval: 60000, // Refetch every minute
  });

  const { data: unitAnalytics, isLoading: unitAnalyticsLoading } = useQuery({
    queryKey: ['hubspot-unit-analytics'],
    queryFn: getUnitInterestAnalytics,
    refetchInterval: 60000,
  });

  const { data: dealPerformance, isLoading: dealPerformanceLoading } = useQuery({
    queryKey: ['hubspot-deal-performance'],
    queryFn: getDealSyncPerformance,
    refetchInterval: 60000,
  });

  // Fetch HubSpot analytics data
  const { data: hubspotAnalytics, isLoading: hubspotAnalyticsLoading } = useQuery({
    queryKey: ['hubspot-analytics'],
    queryFn: getAllHubSpotAnalytics,
    refetchInterval: 60000, // Refetch every minute
  });

  // Configuration status
  const configStatus = {
    portalId: !!hubspotConfig?.portalId,
    formId: !!hubspotConfig?.formId,
    meetingLink: !!meetingUrl,
    apiKey: true, // Stored in Supabase secrets
    webhookSecret: true, // Stored in Supabase secrets
  };

  const isFullyConfigured = Object.values(configStatus).every(status => status);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src="/hubspot-logo.webp" alt="HubSpot" className="h-24 w-auto" />
          <div>
            <h1 className="text-3xl font-bold">HubSpot CRM Integration</h1>
            <p className="text-muted-foreground mt-1">
              Manage HubSpot CRM integration, view analytics, and monitor system health
            </p>
          </div>
        </div>
        <ConnectionStatusBadge 
          isConnected={isFullyConfigured}
          isPartiallyConfigured={Object.values(configStatus).some(s => s) && !isFullyConfigured}
        />
      </div>

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-6' : 'grid-cols-4'} lg:w-auto`}>
          <TabsTrigger 
            value="dashboard" 
            className="gap-2 data-[state=active]:bg-[#FF7A59]/10 data-[state=active]:text-[#FF7A59]"
          >
            <LayoutDashboard className="h-4 w-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </TabsTrigger>
          <TabsTrigger 
            value="analytics" 
            className="gap-2 data-[state=active]:bg-[#FF7A59]/10 data-[state=active]:text-[#FF7A59]"
          >
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Analytics</span>
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger 
              value="system" 
              className="gap-2 data-[state=active]:bg-[#FF7A59]/10 data-[state=active]:text-[#FF7A59]"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">System</span>
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger 
              value="activity" 
              className="gap-2 data-[state=active]:bg-[#FF7A59]/10 data-[state=active]:text-[#FF7A59]"
            >
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Activity</span>
            </TabsTrigger>
          )}
          <TabsTrigger 
            value="deals" 
            className="gap-2 data-[state=active]:bg-[#FF7A59]/10 data-[state=active]:text-[#FF7A59]"
          >
            <Handshake className="h-4 w-4" />
            <span className="hidden sm:inline">Deal Management</span>
          </TabsTrigger>
          <TabsTrigger 
            value="resources" 
            className="gap-2 data-[state=active]:bg-[#FF7A59]/10 data-[state=active]:text-[#FF7A59]"
          >
            <ExternalLink className="h-4 w-4" />
            <span className="hidden sm:inline">Resources</span>
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="mt-0">
            <Suspense fallback={<TabLoadingSkeleton />}>
              <DashboardTab
                leadMetrics={leadMetrics || null}
                dealPerformance={dealPerformance || null}
                isLoading={leadMetricsLoading || dealPerformanceLoading}
                portalId={portalId}
              />
            </Suspense>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-0">
            <Suspense fallback={<TabLoadingSkeleton />}>
              <AnalyticsTab
                leadMetrics={leadMetrics || null}
                unitAnalytics={unitAnalytics || null}
                isLoading={leadMetricsLoading || unitAnalyticsLoading}
                hubspotContacts={hubspotAnalytics?.contacts || null}
                hubspotDeals={hubspotAnalytics?.deals || null}
                hubspotEngagement={hubspotAnalytics?.engagement || null}
                hubspotAnalyticsLoading={hubspotAnalyticsLoading}
              />
            </Suspense>
          </TabsContent>

          {/* System Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="system" className="mt-0">
              <Suspense fallback={<TabLoadingSkeleton />}>
                <SystemTab
                  configStatus={configStatus}
                  portalId={portalId}
                />
              </Suspense>
            </TabsContent>
          )}

          {/* Activity Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="activity" className="mt-0">
              <Suspense fallback={<TabLoadingSkeleton />}>
                <ActivityTab
                  dealPerformance={dealPerformance || null}
                  isLoading={dealPerformanceLoading}
                />
              </Suspense>
            </TabsContent>
          )}

          {/* Deal Management Tab */}
          <TabsContent value="deals" className="mt-0">
            <Suspense fallback={<TabLoadingSkeleton />}>
              <DealManagementTab />
            </Suspense>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="mt-0">
            <Suspense fallback={<TabLoadingSkeleton />}>
              <ResourcesTab portalId={portalId} />
            </Suspense>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
